<template>
  <b-card no-body>
    <b-card-body id="warehouses-card-body">
      <address-book-filter
        :values="filterValues"
        :filter-submitting="isLoading"
        @filter="setFilter"
      />

      <vue-good-table
        mode="remote"
        :sort-options="{ enabled: false }"
        :total-rows="totalRecords"
        :is-loading.sync="isLoading"
        :show-footer="true"
        :pagination-options="{
          enabled: true,
          jumpFirstOrLast: true,
          rowsPerPageLabel: t('Rows per page'),
          ofLabel: t('ofLabel'),
          nextLabel: t('nextLabel'),
          prevLabel: t('prevLabel'),
          dropdownAllowAll: false
        }"
        :rows="rows"
        :columns="getColumns"
        @on-page-change="onPageChange"
        @on-per-page-change="onPerPageChange"
      >
        <template slot="table-row" slot-scope="props">
          <span v-if="props.column.field === 'name'">
            {{ props.row.name || '-' }}
          </span>
          <span v-else-if="props.column.field === 'country'">
            {{ props.row.country.name }}
          </span>
          <span v-else-if="props.column.field === 'state'">
            {{ props.row.state.name }}
          </span>
          <span v-else-if="props.column.field === 'city'">
            {{ props.row.city.name }}
          </span>
          <span v-else-if="props.column.field === 'type'">
            <b-badge pill variant="secondary">
              {{ getAddressTypeName(props.row.type) }}
            </b-badge>
          </span>
          <span v-else-if="props.column.field === 'postalCode'">
            {{
              props.row.postalCode && props.row.postalCode.zipcode
                ? props.row.postalCode.zipcode
                : '-'
            }}
          </span>
          <span v-else-if="props.column.field === 'phoneNumber'">
            {{ props.row.phoneNumber || '-' }}
          </span>
          <span v-else-if="props.column.field === 'email'">
            {{ props.row.email || '-' }}
          </span>
          <span v-else-if="props.column.field === 'attentionName'">
            {{ props.row.attentionName || '-' }}
          </span>
          <span v-else-if="props.column.field === 'status'">
            <b-badge pill :variant="props.row.status ? 'success' : 'danger'">
              {{ props.row.status ? t('Active') : t('Passive') }}
            </b-badge>
          </span>

          <span v-else-if="props.column.field === 'actions'">
            <span class="d-flex justify-content-center table-icon-buttons">
              <b-button
                :id="`edit-button-${props.row.id}`"
                variant="primary"
                type="button"
                size="sm"
                @click="detailWarehouse(props.row.id)"
              >
                <feather-icon icon="Edit3Icon" size="15" />
                <b-tooltip
                  triggers="hover"
                  :target="`edit-button-${props.row.id}`"
                  :title="`${props.row.type === 0 ? t('Edit Address Book') : t('Edit Branch')}`"
                  :delay="{ show: 0, hide: 0 }"
                  boundary="viewport"
                />
              </b-button>
            </span>
          </span>
        </template>
      </vue-good-table>
    </b-card-body>
  </b-card>
</template>

<script>
import { BBadge, BCard, BCardBody, BTooltip, BButton } from 'bootstrap-vue';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import { VueGoodTable } from 'vue-good-table';
import { helper } from '@core/mixins/helper.js';
import { useUtils as useI18nUtils } from '@core/libs/i18n';
import AddressBookFilter from '@/views/pages/address-book/AddressBookFilter.vue';
import useService from '@/auth/service/useService';
import store from '@/store/index';

export default {
  components: {
    VueGoodTable,
    BBadge,
    BCard,
    BCardBody,
    BTooltip,
    BButton,

    // eslint-disable-next-line vue/no-unused-components
    ToastificationContent,
    AddressBookFilter
  },
  mixins: [helper],
  data() {
    return {
      isLoading: false,
      rows: [],
      details: [],
      totalRecords: 0,
      filter: {
        page: 1,
        limit: 10
      },
      filterValues: {
        name: '',
        attentionName: '',
        postalCode: '',
        type: '',
        status: '',
        phoneNumber: '',
        email: '',
        address: ''
      },
      defaultFilterValues: {
        name: '',
        attentionName: '',
        postalCode: '',
        type: '',
        status: '',
        phoneNumber: '',
        email: '',
        address: ''
      },
      refundData: null,
      refundSubmitting: false,
      refundPrice: '',
      refundAllPriceCheckbox: false,
      addressTypes: []
    };
  },
  computed: {
    direction() {
      if (store.state.appConfig.isRTL) {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.dir = true;
        return this.dir;
      }
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.dir = false;
      return this.dir;
    },
    getColumns() {
      if (!this.rows.length) {
        return [];
      }

      return [
        {
          label: this.$t('Name'),
          field: 'name'
        },
        {
          label: this.$t('Address Type'),
          field: 'type'
        },
        {
          label: this.$t('Country'),
          field: 'country'
        },
        {
          label: this.$t('City'),
          field: 'state'
        },
        {
          label: this.$t('District'),
          field: 'city'
        },
        {
          label: this.$t('Address'),
          field: 'address'
        },
        {
          label: this.$t('Postal Code'),
          field: 'postalCode'
        },
        {
          label: this.$t('Phone Number'),
          field: 'phoneNumber'
        },
        {
          label: this.$t('Email'),
          field: 'email'
        },
        {
          label: this.$t('Status'),
          field: 'status'
        },
        {
          label: this.$t('Actions'),
          field: 'actions'
        }
      ];
    }
  },
  async created() {
    this.addressTypes = await this.getAddressType();
    this.loadItems();
  },
  methods: {
    updateParams(newProps) {
      this.filter = { ...this.filter, ...newProps };
    },

    onPageChange(params) {
      this.updateParams({ page: params.currentPage });
      this.loadItems();
    },

    onPerPageChange(params) {
      this.filter.page = 1;
      this.updateParams({ limit: params.currentPerPage });
      this.loadItems();
    },

    // load items is what brings back the rows from server
    loadItems() {
      let requestData = this.filter;

      requestData = {
        ...requestData,
        ...this.getFilteredObject(this.filterValues)
      };

      if (
        requestData.dateFrom &&
        requestData.dateTo &&
        new Date(requestData.dateFrom) > new Date(requestData.dateTo)
      ) {
        this.showErrorMessage(this.$t('Big Date Error'), this.$t('Unsuccessful'));
        return;
      }

      this.isLoading = true;
      useService
        .getAllWarehouses(requestData)
        .then(response => {
          this.rows = response.data;
          this.totalRecords = response.meta.total;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    setFilter(data) {
      this.filterValues = data
        ? { ...this.defaultFilterValues, ...data }
        : { ...this.defaultFilterValues };
      this.filter.page = 1;
      this.loadItems();
    },
    detailWarehouse(id) {
      this.$router.push(`/address_book/${id}`);
    },
    getAddressTypeName(type) {
      const typeName = type;
      const typeElement = this.addressTypes.filter(x => x.value === parseInt(type));

      if (typeElement && typeElement.length > 0) {
        return typeElement[0].info;
      }

      return typeName;
    }
  },
  setup() {
    const { t } = useI18nUtils();

    return {
      t
    };
  }
};
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table.scss';
</style>
